import { useTranslation } from 'react-i18next';
import { Col, Form, Input, Row, Select } from 'antd';
import React, { useMemo } from 'react';
import allCountries from 'country-region-data/data.json';
import { collect } from 'collect.js';
import CountrySelect from './CountrySelect';
import { useUpdateEffect } from '../../../hooks/utility/useUpdateEffect';

export default function AddressFields({ title, prefix, data, onChange, labels, useGrid, className }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Form:Address:AddressFields' });

  const constructField = (field) => (prefix ? `${prefix}_${field}` : field);
  const fields = useMemo(
    () => ({
      address: constructField('address'),
      city: constructField('city'),
      state: constructField('state'),
      country: constructField('country'),
      zip_code: constructField('zip_code'),
    }),
    [prefix],
  );

  const countryCode = data[fields.country];
  const regionOptions = useMemo(
    () =>
      collect(collect(allCountries).firstWhere('countryShortCode', countryCode)?.regions ?? [])
        .map((item) => ({ label: item.name, value: item.name }))
        .toArray(),
    [countryCode],
  );

  useUpdateEffect(() => {
    if (!collect(regionOptions).firstWhere('value', data[fields.state])) {
      onChange(fields.state, null);
    }
  }, [countryCode]);

  return (
    <div className={className}>
      {Boolean(title) && <h6 className="underline my-md">{title}</h6>}
      <Form.Item label={labels?.address ?? t('form.address.label')} required>
        <Input
          placeholder={t('form.address.placeholder')}
          value={data[fields.address] ?? ''}
          onChange={(e) => onChange(fields.address, e.target.value)}
          aria-autocomplete="none"
          autocomplete="none"
        />
      </Form.Item>
      <Row gutter={12}>
        <Col span={useGrid ? 12 : 24}>
          <Form.Item label={labels?.city ?? t('form.city.label')} required>
            <Input
              placeholder={t('form.city.placeholder')}
              value={data[fields.city] ?? ''}
              onChange={(e) => onChange(fields.city, e.target.value)}
              aria-autocomplete="none"
              autocomplete="none"
            />
          </Form.Item>
        </Col>
        <Col span={useGrid ? 12 : 24}>
          <Form.Item label={labels?.zip_code ?? t('form.zip_code.label')} required>
            <Input
              placeholder={t('form.zip_code.placeholder')}
              value={data[fields.zip_code] ?? ''}
              onChange={(e) => onChange(fields.zip_code, e.target.value)}
              aria-autocomplete="none"
              autocomplete="none"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={useGrid ? 12 : 24}>
          <Form.Item label={labels?.country ?? t('form.country.label')} required>
            <CountrySelect value={data[fields.country]} onChange={(value) => onChange(fields.country, value)} />
          </Form.Item>
        </Col>
        <Col span={useGrid ? 12 : 24}>
          <Form.Item label={labels?.state ?? t('form.state.label')} required>
            <Select
              value={data[fields.state]}
              options={regionOptions}
              onChange={(value) => onChange(fields.state, value)}
              showSearch={{ optionFilterProp: 'label' }}
              placeholder={t('form.state.placeholder')}
              aria-autocomplete="none"
              autocomplete="none"
            />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
}
